import { Fragment, ReactNode, useState } from 'react';

import { IconButton, Row } from 'components/Blocks';
import { ChevronLeftIcon, ChevronRightIcon } from 'components/Icons';

import { cn, ColorOrCustom, resolveColorClass, ResponsiveBreakpoint } from 'styles';
import { useIsBreakpoint } from 'hooks';

type DynamicContent<Params> = ReactNode | ((params: Params) => ReactNode);

interface StepperProps {
  children?: never;

  lineActiveColor?: ColorOrCustom;
  lineInactiveColor?: ColorOrCustom;

  className?: string;
  stepClassName?: string;
  activeStepClassName?: string;

  steps: Array<{
    content: DynamicContent<{ active: boolean; currentStep: number }>;
    activeContent: DynamicContent<{ next(): void }>;
    disabled?: boolean;
  }>;

  /**
   * Breakpoint to which *only the current step* is rendered with < > control buttons
   */
  responsiveBreakpoint?: ResponsiveBreakpoint;
}

interface StepProps {
  disabled?: boolean;
  stepClassName?: string;
  activeStepClassName?: string;
  isActive?: boolean;
  isCurrent: boolean;
  content: DynamicContent<{ active: boolean; currentStep: number }>;

  step: number;
  onClick?: () => void;
}

function Step({
  activeStepClassName,
  disabled,
  isActive,
  stepClassName,
  isCurrent,
  onClick,
  content,
  step,
}: StepProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-2 bg-transparent outline-[0]',
        stepClassName,
        isActive && activeStepClassName,
      )}
    >
      {typeof content === 'function' ? content({ active: isCurrent, currentStep: step }) : content}
    </button>
  );
}

function Stepper({
  lineActiveColor = 'primary',
  lineInactiveColor = 'border',
  steps,
  className,
  responsiveBreakpoint = 'SMALL_PHONE',
  ...props
}: StepperProps) {
  const isResponsive = useIsBreakpoint(responsiveBreakpoint);

  const [step, setStep] = useState(0);

  const previousStep = steps[step - 1];
  const currentStep = steps[step]!;
  const nextStep = steps[step + 1];

  const next = (): void => setStep((old) => (old + 1 < steps.length ? old + 1 : old));

  return (
    <>
      <Row fullWidth justifyContent="space-between" className={className}>
        {isResponsive ? (
          <>
            <IconButton
              disabled={!previousStep || previousStep.disabled}
              className={
                !previousStep || previousStep.disabled ? 'disabled:opacity-0' : 'text-primary'
              }
              onClick={() => setStep((s) => Math.min(s - 1, 0))}
            >
              <ChevronLeftIcon />
            </IconButton>

            <Step
              {...props}
              disabled={currentStep.disabled}
              step={step}
              content={currentStep.content}
              isCurrent
              isActive
            />

            <IconButton
              disabled={!nextStep || nextStep.disabled}
              className={!nextStep || nextStep.disabled ? 'disabled:opacity-0' : 'text-primary'}
              onClick={() => setStep((s) => Math.min(s + 1, steps.length))}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        ) : (
          <>
            {steps.map(({ content, disabled }, index) => (
              <Fragment key={`stepper-${index}`}>
                {!!index && (
                  <span
                    className={cn(
                      'h-1 w-full rounded-3xl transition-colors phone:max-w-1',
                      step >= index
                        ? resolveColorClass(lineActiveColor, 'bg')
                        : resolveColorClass(lineInactiveColor, 'bg'),
                    )}
                  />
                )}

                <Step
                  {...props}
                  isActive={step >= index}
                  onClick={() => setStep(index)}
                  disabled={disabled}
                  step={step}
                  content={content}
                  isCurrent={index === step}
                />
              </Fragment>
            ))}
          </>
        )}
      </Row>

      {typeof currentStep.activeContent === 'function'
        ? currentStep.activeContent({ next })
        : currentStep.activeContent}
    </>
  );
}

export default Stepper;
